import React, { useState } from 'react';
import { api } from '../api/nocode';
import { TABLES } from '../api/tables';
import { useAcademy } from '../context/AcademyContext';
import Card from './ui/Card';
import SafeIcon from '../common/SafeIcon';

export default function RunStateControls() {
  const { runs, isEnabled, refreshAcademy } = useAcademy();
  const [updatingId, setUpdatingId] = useState('');
  const [error, setError] = useState('');

  const updateRunState = async (run) => {
    const classInSession = !isEnabled(run);
    setUpdatingId(String(run.id));
    setError('');

    try {
      await api.put(TABLES.runs, run.id, {
        is_active: classInSession ? 1 : 0,
        status: classInSession ? 'active' : 'inactive'
      });
      await refreshAcademy();
    } catch (updateError) {
      setError(
        updateError?.message || 'Unable to update the class state.'
      );
    } finally {
      setUpdatingId('');
    }
  };

  if (!runs.length) return null;

  return (
    <Card className="mb-8 border-2 border-amber bg-amber/10">
      <div className="mb-5 flex items-start gap-3">
        <div className="rounded-full bg-dark-green p-3 text-white">
          <SafeIcon name="Power" className="text-xl" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-dark-green">
            Event Run Class Availability
          </h2>
          <p className="mt-1 text-sm text-muted">
            In-session classes open every module immediately. Upcoming classes
            show enrolled students a welcome screen while modules remain locked.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {runs.map((run) => {
          const classInSession = isEnabled(run);
          const isUpdating = updatingId === String(run.id);

          return (
            <div
              key={run.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-tan bg-white p-4"
            >
              <div>
                <div className="font-bold text-dark-green">
                  {run.title || run.name || 'Event Run'}
                </div>
                <div className={`mt-1 flex items-center gap-2 text-sm font-semibold ${
                  classInSession ? 'text-emerald-700' : 'text-amber'
                }`}>
                  <SafeIcon
                    name={classInSession ? 'Radio' : 'Clock'}
                  />
                  {classInSession
                    ? 'Class in Session'
                    : 'Class Upcoming'}
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={classInSession}
                aria-label={`Set ${run.title || run.name} to ${
                  classInSession ? 'Class Upcoming' : 'Class in Session'
                }`}
                disabled={isUpdating}
                onClick={() => updateRunState(run)}
                className={`relative h-8 w-14 shrink-0 rounded-full p-1 transition-colors disabled:cursor-wait disabled:opacity-60 ${
                  classInSession ? 'bg-emerald-600' : 'bg-gray-300'
                }`}
              >
                <span className={`block h-6 w-6 rounded-full bg-white shadow transition-transform ${
                  classInSession ? 'translate-x-6' : ''
                }`} />
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}